import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Eye, EyeOff } from "lucide-react";
import AuthShell from "@/components/AuthShell";
import GoogleButton from "@/components/GoogleButton";
import { useAuth } from "@/context/AuthContext";
import { errorMessage } from "@/lib/api";

export default function LoginPage() {
  const { login } = useAuth();
  const nav = useNavigate();
  const location = useLocation();
  const [form, setForm] = useState({ email: "", password: "" });
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const rejected = location?.state?.rejected;

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      const u = await login(form.email.trim(), form.password);
      const from = location.state?.from;
      if (u.role === "admin" || u.role === "super_admin") nav(from || "/admin", { replace: true });
      else nav(from || "/dashboard", { replace: true });
    } catch (err) {
      setError(errorMessage(err));
    } finally { setLoading(false); }
  };

  return (
    <AuthShell
      title="Welcome back."
      subtitle="Sign in"
      footer={<div>New to Steadfast? <Link to="/register" className="link-underline font-semibold text-foreground">Request access</Link></div>}
    >
      {rejected && (
        <div data-testid="rejected-banner" className="mb-5 p-3 border border-destructive/30 bg-destructive/5 text-destructive text-[13px]">
          Your account has been rejected. Please contact support.
        </div>
      )}
      <GoogleButton />
      <div className="my-6 flex items-center gap-3 text-[11px] uppercase tracking-[0.22em] text-muted-foreground">
        <div className="flex-1 h-px bg-border" /> or with email <div className="flex-1 h-px bg-border" />
      </div>
      <form onSubmit={submit} className="space-y-4">
        <div>
          <label className="block text-[12px] font-semibold uppercase tracking-wider text-muted-foreground mb-1.5">Email</label>
          <input
            data-testid="login-email-input"
            autoFocus
            type="email"
            required
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            className="w-full h-11 px-3 border border-border bg-white text-foreground focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary"
          />
        </div>
        <div>
          <div className="flex items-center justify-between mb-1.5">
            <label className="block text-[12px] font-semibold uppercase tracking-wider text-muted-foreground">Password</label>
            <Link to="/forgot-password" className="text-[12px] font-semibold text-primary hover:underline">Forgot?</Link>
          </div>
          <div className="relative">
            <input
              data-testid="login-password-input"
              type={showPw ? "text" : "password"}
              required
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              className="w-full h-11 px-3 pr-10 border border-border bg-white text-foreground focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary"
            />
            <button type="button" onClick={() => setShowPw((v) => !v)} className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 text-muted-foreground hover:text-foreground" aria-label="Toggle password">
              {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>
        </div>
        {error && <div data-testid="login-error" className="text-[13px] text-destructive">{error}</div>}
        <button
          data-testid="login-submit-button"
          disabled={loading}
          type="submit"
          className="w-full h-11 bg-primary text-primary-foreground font-semibold text-[14px] hover:bg-[#001d6b] disabled:opacity-60"
        >
          {loading ? "Signing in..." : "Sign in"}
        </button>
      </form>
    </AuthShell>
  );
}
